import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Lock, Eye, EyeOff, CheckCircle, ArrowLeft, ShieldCheck, Globe, Instagram, LogIn } from 'lucide-react';
import Auth3DShowcase from './Auth3DShowcase';
import './login.css';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reset Password — same cinematic split layout as LoginPage. Token comes
 * from the email link's `?token=` query param. On success the user sees
 * a confirmation card with a primary "Go to Sign In" button AND a
 * 5-second auto-redirect, so they're never stranded on the success screen.
 */
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resetToken = searchParams.get('token');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [redirectIn, setRedirectIn] = useState(5);

  // Auto-redirect to /login 5 s after a successful reset. Cancellable by
  // the user clicking the explicit "Go to Sign In" button below.
  useEffect(() => {
    if (!success) return undefined;
    const interval = setInterval(() => {
      setRedirectIn((s) => {
        if (s <= 1) {
          clearInterval(interval);
          navigate('/login');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || 'Reset failed. The link may have expired.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Invalid / missing token — keep brand layout, friendly message.
  if (!resetToken) {
    return (
      <div className="min-h-screen w-full max-w-full bg-black grid grid-cols-1 lg:grid-cols-[1.1fr_minmax(420px,0.9fr)] overflow-x-hidden">
        <Auth3DShowcase />
        <div className="relative flex items-center justify-center p-6 sm:p-10 auth-grain reset-aurora"
             style={{ background: 'radial-gradient(ellipse at 20% 10%, #1a1340 0%, #0a0d1c 35%, #05060f 70%, #000 100%)' }}>
          <div className="reset-aurora__orb reset-aurora__orb--violet" />
          <div className="reset-aurora__orb reset-aurora__orb--cyan" />
          <div className="reset-aurora__orb reset-aurora__orb--pink" />
          <div className="relative w-full max-w-md auth-fade-up">
            <div className="flex flex-col items-center mb-8">
              <Link to="/" className="flex items-center gap-3 mb-2 select-none no-underline group">
                <img src="/branding/avst-logo.png" alt=""
                     className="h-14 w-auto select-none drop-shadow-[0_0_18px_rgba(56,189,248,0.35)] transition-transform group-hover:scale-105" draggable={false} />
                <span className="text-white font-bold tracking-tight text-xl">AV Simulation Studio</span>
              </Link>
            </div>
            <div className="auth-glass rounded-2xl p-7 text-center">
              <h2 className="text-lg font-semibold text-white mb-2">Invalid Link</h2>
              <p className="text-zinc-400 text-sm mb-4">
                This reset link is missing a token or has expired. Please request a new one.
              </p>
              <Link to="/forgot-password">
                <Button className="auth-submit h-11 rounded-xl text-white font-semibold">Request a new link</Button>
              </Link>
              <div className="mt-4">
                <Link to="/login" className="text-xs text-zinc-400 hover:text-cyan-300 transition-colors tracking-wider">
                  <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to Sign In
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full max-w-full bg-black grid grid-cols-1 lg:grid-cols-[1.1fr_minmax(420px,0.9fr)] overflow-x-hidden"
      data-testid="reset-password-page"
    >
      <Auth3DShowcase />

      <div
        className="relative flex items-center justify-center p-6 sm:p-10 auth-grain reset-aurora"
        style={{ background: 'radial-gradient(ellipse at 20% 10%, #1a1340 0%, #0a0d1c 35%, #05060f 70%, #000 100%)' }}
      >
        {/* Aurora orbs — richer, more saturated than other auth pages */}
        <div className="reset-aurora__orb reset-aurora__orb--violet" />
        <div className="reset-aurora__orb reset-aurora__orb--cyan" />
        <div className="reset-aurora__orb reset-aurora__orb--pink" />
        {/* Floating shield+lock centerpiece (sits BEHIND the glass card) */}
        <div className="reset-aurora__shield" aria-hidden="true">
          <svg viewBox="0 0 200 220" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="shieldGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"  stopColor="#22d3ee" stopOpacity="0.55" />
                <stop offset="60%" stopColor="#a855f7" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#ec4899" stopOpacity="0.4" />
              </linearGradient>
              <radialGradient id="shieldGlow" cx="50%" cy="40%" r="60%">
                <stop offset="0%"  stopColor="#a855f7" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="100" cy="100" r="92" fill="url(#shieldGlow)" />
            <path d="M100 10 L185 42 L185 110 C185 162 145 198 100 210 C55 198 15 162 15 110 L15 42 Z"
                  fill="none" stroke="url(#shieldGrad)" strokeWidth="1.6" strokeLinejoin="round" />
            <path d="M100 22 L173 50 L173 110 C173 154 140 184 100 196 C60 184 27 154 27 110 L27 50 Z"
                  fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.8" strokeDasharray="2 4" />
            {/* lock body */}
            <rect x="78" y="98" width="44" height="42" rx="6"
                  fill="rgba(8,10,22,0.55)" stroke="url(#shieldGrad)" strokeWidth="1.4" />
            {/* shackle */}
            <path d="M85 98 L85 84 C85 76 91 70 100 70 C109 70 115 76 115 84 L115 98"
                  fill="none" stroke="url(#shieldGrad)" strokeWidth="1.6" strokeLinecap="round" />
            {/* keyhole */}
            <circle cx="100" cy="115" r="3.6" fill="#22d3ee" opacity="0.8" />
            <rect x="98.4" y="115" width="3.2" height="10" fill="#22d3ee" opacity="0.8" />
          </svg>
        </div>

        <div className="relative w-full max-w-md auth-fade-up">
          <div className="flex flex-col items-center mb-8">
            <Link
              to="/"
              data-testid="reset-brand-link"
              className="flex items-center gap-3 mb-2 select-none no-underline group"
              aria-label="AV Simulation Studio — home"
            >
              <img src="/branding/avst-logo.png" alt=""
                   className="h-14 w-auto select-none drop-shadow-[0_0_18px_rgba(56,189,248,0.35)] transition-transform group-hover:scale-105" draggable={false} />
              <span className="text-white font-bold tracking-tight text-xl">AV Simulation Studio</span>
            </Link>
            <p className="text-[11px] text-zinc-500 tracking-[0.28em] uppercase">Powered by Ideation Audio Visual</p>
          </div>

          <div className="auth-glass rounded-2xl p-7">
            {success ? (
              <div className="text-center py-4" data-testid="reset-success">
                <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">Password updated</h2>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                  Your new password is now active. You can sign in with it right away.
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="auth-submit w-full h-11 rounded-xl text-white font-semibold"
                  data-testid="reset-go-to-login-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" /> Go to Sign In
                </Button>
                <p className="mt-4 text-[11px] text-zinc-500 tracking-wide">
                  Auto-redirecting in {redirectIn}s…
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-white">Reset Password</h2>
                  <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-cyan-300/80">
                    <ShieldCheck className="w-3 h-3" /> Secure
                  </span>
                </div>
                <p className="text-zinc-400 text-sm mb-6">Choose a strong new password — at least 6 characters.</p>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg p-3 mb-4">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-zinc-300 text-xs tracking-widest uppercase">New Password</Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min 6 characters"
                        className="auth-input pl-10 pr-10 h-11 rounded-xl placeholder:text-zinc-600"
                        required
                        data-testid="reset-new-password-input"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-cyan-300 transition-colors">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-xs tracking-widest uppercase">Confirm Password</Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm password"
                        className="auth-input pl-10 h-11 rounded-xl placeholder:text-zinc-600"
                        required
                        data-testid="reset-confirm-password-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="auth-submit w-full h-11 rounded-xl text-white font-semibold disabled:opacity-70"
                    data-testid="reset-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Resetting…
                      </span>
                    ) : 'Reset Password'}
                  </Button>
                </form>

                <div className="mt-5 text-center">
                  <Link to="/login" className="text-xs text-zinc-400 hover:text-cyan-300 transition-colors tracking-wider">
                    <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to Sign In
                  </Link>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 text-center space-y-3">
            <div className="flex items-center justify-center gap-4 text-[11px] text-zinc-500">
              <a href="https://www.ideationav.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition-colors">
                <Globe className="w-3 h-3" /> www.ideationav.com
              </a>
              <span className="text-zinc-700">•</span>
              <a href="https://www.instagram.com/avsimulationtool/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-fuchsia-300 transition-colors">
                <Instagram className="w-3 h-3" /> @avsimulationtool
              </a>
            </div>
            <p className="text-[10px] text-zinc-600 leading-relaxed">© 2026 Ideation Audio Visual. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
